/* eslint-disable react-refresh/only-export-components */
import Bugsnag from '@bugsnag/js'
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, UseQueryArgs } from 'urql'
import { Membership, User } from '../../../gql/graphql'
import {
  __PARTIAL__APP_CREW_SCHEDULE,
  __PARTIAL__APP_LEAD_SCHEDULE,
  APP_CREW_SCHEDULE,
  APP_LEAD_SCHEDULE
} from '../../_constants/permissions'
import { cacheDatabaseName } from '../../_lib/graphql/exchanges/cacheExchange'
import { loginUserMutation, logoutUserMutation } from '../../_lib/graphql/mutations'
import { readMyselfQuery } from '../../_lib/graphql/queries'
import { Spinner } from '../../_ui'
import { MissingMembershipAnswersDialog } from '../MissingMembershipAnswersDialog'
import { MissingProfileInformationDialog } from '../MissingProfileInformationDialog'
import { useSchedule } from '../ScheduleContext'
import { setLoginCookie } from '../../_lib/loginCookie'
import { useServiceWorker } from '../ServiceWorker'
import { useSnackbarPush } from '../SnackbarContext/SnackbarContext'
import { SwUpdateDialog } from '../SwUpdateDialog'

const REQUIRED_PROFILE_INFORMATION = [
  'city',
  'email',
  'firstName',
  'phone',
  'street',
  'surname',
  'zip'
]

interface MyselfContextType extends User {
  hasHelperSchedulePermissions?: boolean
  IsLoggedIn?: boolean,
  login: (email: string, password: string, rememberMe: boolean) => void
  logout?: () => void,
  canAccess: (code: string | string[]) => boolean
  refetchUser: () => void
}

const MyselfContext = createContext<MyselfContextType | null>(null);

export const useMyself = () => useContext(MyselfContext)

type PropTypes = {
  children: ReactNode
  loggedOut: ReactNode
}

const UserContextProvider = ({ children, loggedOut }: PropTypes) => {
  const { isUpdateAvailable, updateAssets } = useServiceWorker()
  const [hasData, setHasData] = useState(true)
  const isFirstLoading = useRef(true)
  const { refetchCurrentSeason } = useSchedule() || {}
  const { pushSuccess, pushError } = useSnackbarPush()

  const [myself, refetchUser] = useQuery({ query: readMyselfQuery } as UseQueryArgs);
  const [loginState, executeLoginUser] = useMutation(loginUserMutation)
  const [logoutState, executeLogoutUser] = useMutation(logoutUserMutation)

  const maskEmail = useCallback((email?: string | null): string | undefined => {
    if (!email || email.length <= 10) return email || undefined;

    const [localPart, domain] = email.split('@');
    if (!domain) return email;

    const visibleStart = localPart.slice(0, 5);
    const visibleEnd = localPart.slice(-5);
    const maskedMiddle = '*'.repeat(Math.max(0, localPart.length - 10));

    return `${visibleStart}${maskedMiddle}${visibleEnd}@${domain}`;
  }, []);

  useEffect(() => {
    if (!isFirstLoading.current) {
      setHasData(myself.data !== undefined && myself.data?.readMyself)
    } else {
      isFirstLoading.current = false
    }
  }, [myself])

  useEffect(() => {
    if (hasData) {
      window.scrollTo(0, 0)
      document.scrollingElement?.scrollTo(0, 0)
    }
  }, [hasData])

  useEffect(() => {
    Bugsnag.setUser(
      myself.data?.readMyself?.id,
      maskEmail(myself.data?.readMyself?.email),
      myself.data?.readMyself?.firstName
    )
  }, [maskEmail, myself.data?.readMyself?.email, myself.data?.readMyself?.firstName, myself.data?.readMyself?.id])

  const login = useCallback((email: string, password: string, rememberMe: boolean) => {
    executeLoginUser({
      input: {
        email,
        password,
        rememberMe
      }
    }).then(res => {
      if (res.error) {
        pushError(res.error.message)
      } else {
        localStorage.setItem('token', res.data?.loginUser?.access_token || '')
        localStorage.setItem('refresh_token', res.data?.loginUser?.refresh_token || '')

        const time = Math.round(new Date().getTime() / 1000)
        const expiredTimestamp = time + parseInt(res.data?.loginUser?.expiry || '', 10)
        localStorage.setItem('expiredTimestamp', expiredTimestamp.toString());
        setLoginCookie(true)
        pushSuccess('Login erfolgreich')
        refetchUser()

        if (refetchCurrentSeason) {
          refetchCurrentSeason()
        }
      }
    })
  }, [executeLoginUser, pushError, pushSuccess, refetchUser, refetchCurrentSeason])

  const logout = useCallback(() => {
    executeLogoutUser({}).finally(() => {
      indexedDB.deleteDatabase(cacheDatabaseName)
      localStorage.removeItem('token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('expiredTimestamp')
      localStorage.removeItem('debug-user-id')
      setLoginCookie(false)
      refetchUser()
    })
  }, [executeLogoutUser, refetchUser])

  useEffect(() => {
    const listener = () => {
      logout()
    }

    document.addEventListener('logoutUser', listener)
    return () => document.removeEventListener('logoutUser', listener)
  }, [logout])

  const canAccess = useCallback((code: string | string[]) => {
    const currentMember = myself.data?.readMyself
    if (currentMember && currentMember.permissions) {
      if (currentMember.permissions.findIndex((permission: string) => permission && permission === 'ADMIN') >= 0) {
        return true
      }

      return typeof code === 'object'
        ? currentMember.permissions.findIndex((permission: string) => permission && code.includes(permission)) >= 0
        : currentMember.permissions.findIndex((permission: string) => permission && permission === code) >= 0
    }
    return false
  }, [myself])

  const missingRequiredFieldsGroup = useMemo(() => {
    if (myself.stale || myself.fetching) {
      return null
    }

    return myself.data?.readMyself?.memberships?.reduce((groupID: string | null, membership: Membership) => {
      return groupID || (((membership?.emptyRequiredFields || [])?.length || membership.emptyRequiredNotificationFields?.length) ? membership.id : null)
    }, null)
  }, [myself])

  const missingRequiredFieldsGroupTitle = useMemo(() => {
    return myself.data?.readMyself?.memberships?.reduce((groupTitle: string | null, membership: Membership) => {
      return groupTitle || ((membership?.emptyRequiredFields || [])?.length ? membership.userGroup?.title || '' : null)
    }, '')
  }, [myself])

  const missingRequiredProfileInformation = useMemo(() => {
    if (!myself.data) {
      return false;
    }

    if (myself.data?.readMyself?.requiresProfileUpdate) {
      return true
    }

    return REQUIRED_PROFILE_INFORMATION.reduce((acc, property) => {
      if (!myself.data?.readMyself?.[property] || myself.data?.readMyself?.[property] === '') {
        return true
      }

      return acc
    }, false)
  }, [myself])

  // Solange die Benutzerabfrage nichts geliefert hat, ist noch nicht entschieden, ob jemand ausgeloggt ist
  const isResolving = myself.fetching || myself.stale || (myself.data === undefined && !myself.error)

  const hasHelperSchedulePermissions = useMemo(() => {
    if (canAccess(APP_CREW_SCHEDULE)
      || canAccess(APP_LEAD_SCHEDULE)
      || canAccess(__PARTIAL__APP_CREW_SCHEDULE)
      || canAccess(__PARTIAL__APP_LEAD_SCHEDULE)) {
      return true
    }

    return myself.data?.readMyself?.isInTargetGroupAndConfirmed || false

    // Logik bis 2025
    // if (season && (myself.data?.readMyself?.sections || myself.data?.readMyself?.sectionRoles)) {
    //   return (myself.data?.readMyself?.sections || []).filter((s: any) => s.season === season.id).length > 0
    //     || (myself.data?.readMyself?.sectionRoles || []).filter((s: any) => s.season === season.id).length > 0
    // }
    //
    // return false
  }, [canAccess, myself.data?.readMyself?.isInTargetGroupAndConfirmed])

  useEffect(() => {
    const handleLogout = () => {
      setHasData(false)
    }

    document.addEventListener("loginError", () => handleLogout(), false)
  }, [])

  return (
    <MyselfContext.Provider
      value={{
        ...myself.data?.readMyself,
        IsLoggedIn: hasData,
        hasHelperSchedulePermissions,
        login,
        logout,
        canAccess,
        refetchUser
      }}
    >
      {/* TODO: new login handling */}
      {hasData
        ? (
          <>
            {children}
            <MissingMembershipAnswersDialog
              open={missingRequiredFieldsGroup !== null && !missingRequiredProfileInformation}
              title={missingRequiredFieldsGroupTitle}
              membershipID={missingRequiredFieldsGroup}
            />
            <MissingProfileInformationDialog
              open={missingRequiredProfileInformation}
              profile={myself.data?.readMyself}
            />
          </>
        ) : isResolving ? null : loggedOut
      }
      {(myself.stale || myself.fetching || logoutState.fetching || loginState.fetching) && (
        <div className={'fixed top-0 w-screen h-full z-40 backdrop-blur-sm bg-black/30'}>
          <div className={'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'}>
            <Spinner size={'w-16 h-16'} />
          </div>
        </div>
      )}
      {isUpdateAvailable && updateAssets && (
        <SwUpdateDialog />
      )}
    </MyselfContext.Provider>
  );
}

export default UserContextProvider;
